import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import DictationInput from '../DictationInput';
import { toast } from 'sonner';
import { Building, BookOpen, MessageSquare, Sparkles, Loader2 } from 'lucide-react';

const PREP_TIP_PROMPTS = [
  { id: 'prep_story', title: 'Prep your story', prompt: 'In 3-4 sentences, how would you introduce yourself and your journey?' },
  { id: 'dress_professional', title: 'Dress professionally', prompt: 'What outfit will help you feel confident and professional for this interview?' },
  { id: 'assertive', title: 'Be assertive', prompt: 'Where can you show confidence and clarity in your answers?' },
  { id: 'arrive_early', title: 'Arrive early', prompt: 'What is your plan to arrive 10-15 minutes early?' },
  { id: 'ask_questions', title: 'Ask questions', prompt: 'What is one thoughtful question you want to ask the interviewer?' }
];

const BACKGROUND_PROMPTS = [
  { id: 'be_honest', title: 'Be honest about your background', prompt: 'What is the hardest thing an employer could find out about you?' },
  { id: 'focus_growth', title: 'Focus on growth', prompt: 'How have you grown over the last 5-10 years?' },
  { id: 'highlight_skills', title: 'Highlight skills', prompt: 'What are things you do really well without trying too hard?' },
  { id: 'show_commitment', title: 'Show commitment', prompt: 'Tell me something you started and completed that made you proud.' }
];

export default function PrepTipsTab({ api, resources }) {
  const [prepCompany, setPrepCompany] = useState('');
  const [prepResult, setPrepResult] = useState(null);
  const [prepLoading, setPrepLoading] = useState(false);
  const [prepResponses, setPrepResponses] = useState({});
  const [prepFeedback, setPrepFeedback] = useState({});
  const [prepFeedbackLoading, setPrepFeedbackLoading] = useState({});

  const requestCoachFeedback = async (key, prompt, response, context = 'interview') => {
    if (!response?.trim()) return toast.error('Please add your response first');
    setPrepFeedbackLoading(prev => ({ ...prev, [key]: true }));
    try {
      const res = await api.post('/ai/prep-coach', { prompt, response, company: prepCompany, context });
      setPrepFeedback(prev => ({ ...prev, [key]: res.data.feedback }));
    } catch {
      toast.error('Unable to generate feedback');
    } finally {
      setPrepFeedbackLoading(prev => ({ ...prev, [key]: false }));
    }
  };

  const handleResearch = async () => {
    setPrepLoading(true);
    try {
      const r = await api.post('/ai/interview-prep', { company_name: prepCompany });
      setPrepResult(r.data);
      toast.success('Prep guide ready');
    } catch {
      toast.error('Unable to generate prep');
    } finally {
      setPrepLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <Card className="border-0 shadow-sm rounded-2xl">
        <CardHeader><CardTitle className="text-base font-semibold flex items-center gap-2"><Building className="w-4 h-4 text-sky-600" />Interview Preparation by Company</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">Enter a company name and get customized preparation tips.</p>
          <div className="flex gap-2">
            <Input value={prepCompany} onChange={e => setPrepCompany(e.target.value)} placeholder="Company name" className="flex-1" data-testid="company-prep-input" />
            <Button disabled={!prepCompany || prepLoading} className="bg-sky-600 hover:bg-sky-700 text-white rounded-full gap-2 shrink-0" data-testid="company-prep-btn" onClick={handleResearch}>
              {prepLoading ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />} Research
            </Button>
          </div>
          {prepResult && (
            <div className="space-y-3">
              {prepResult.company_overview && <div className="bg-sky-50 rounded-xl p-4 border border-sky-200"><p className="text-xs font-semibold text-sky-600 mb-1">About the Company</p><p className="text-sm">{prepResult.company_overview}</p>{prepResult.mission && <p className="text-sm italic mt-2 text-sky-700">{prepResult.mission}</p>}</div>}
              {prepResult.interview_tips?.length > 0 && <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-200"><p className="text-xs font-semibold text-emerald-600 mb-2">Interview Tips</p>{prepResult.interview_tips.map((t, i) => <p key={`item-${i}`} className="text-sm mb-1">- {t}</p>)}</div>}
              {prepResult.clothing_suggestions && <div className="bg-amber-50 rounded-xl p-4 border border-amber-200"><p className="text-xs font-semibold text-amber-600 mb-1">What to Wear</p><p className="text-sm">{prepResult.clothing_suggestions}</p></div>}
              {prepResult.questions_to_ask?.length > 0 && <div className="bg-violet-50 rounded-xl p-4 border border-violet-200"><p className="text-xs font-semibold text-violet-600 mb-2">Questions to Ask Them</p>{prepResult.questions_to_ask.map((q, i) => <p key={`item-${i}`} className="text-sm mb-1">- {q}</p>)}</div>}
              {prepResult.background_advice && <div className="bg-teal-50 rounded-xl p-4 border border-teal-200"><p className="text-xs font-semibold text-teal-600 mb-1">Discussing Your Background</p><p className="text-sm">{prepResult.background_advice}</p></div>}
            </div>
          )}
        </CardContent>
      </Card>

      {resources && (
        <>
          <Card className="border-0 shadow-sm rounded-2xl">
            <CardHeader className="pb-3"><CardTitle className="text-base font-semibold flex items-center gap-2"><BookOpen className="w-4 h-4 text-teal-600" /> Prep Tips + Company Prep</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              {PREP_TIP_PROMPTS.map(item => (
                <div key={item.id} className="p-3 rounded-xl bg-muted/30 space-y-2">
                  <p className="font-medium text-sm">{item.title}</p>
                  <p className="text-xs text-muted-foreground">{item.prompt}</p>
                  <DictationInput value={prepResponses[item.id] || ''} onChange={(cleaned) => setPrepResponses(prev => ({ ...prev, [item.id]: cleaned }))} rows={2} placeholder="Speak or type your response..." data-testid={`prep-response-${item.id}`} />
                  <Button size="sm" className="rounded-full" onClick={() => requestCoachFeedback(item.id, item.prompt, prepResponses[item.id] || '', 'prep_tip')} disabled={prepFeedbackLoading[item.id]} data-testid={`prep-feedback-${item.id}`}>
                    {prepFeedbackLoading[item.id] ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />} Get Feedback
                  </Button>
                  {prepFeedback[item.id] && <p className="text-xs text-emerald-700" data-testid={`prep-feedback-text-${item.id}`}>{prepFeedback[item.id]}</p>}
                </div>
              ))}
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm rounded-2xl">
            <CardHeader className="pb-3"><CardTitle className="text-base font-semibold flex items-center gap-2"><MessageSquare className="w-4 h-4 text-amber-600" /> Discussing Your Background</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              {BACKGROUND_PROMPTS.map(item => (
                <div key={item.id} className="p-3 rounded-xl bg-amber-50 space-y-2">
                  <p className="font-medium text-sm text-amber-900">{item.title}</p>
                  <p className="text-xs text-amber-700">{item.prompt}</p>
                  <DictationInput value={prepResponses[item.id] || ''} onChange={(cleaned) => setPrepResponses(prev => ({ ...prev, [item.id]: cleaned }))} rows={2} placeholder="Your response..." data-testid={`background-response-${item.id}`} />
                  <Button size="sm" className="rounded-full" onClick={() => requestCoachFeedback(item.id, item.prompt, prepResponses[item.id] || '', 'background')} disabled={prepFeedbackLoading[item.id]} data-testid={`background-feedback-${item.id}`}>
                    {prepFeedbackLoading[item.id] ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />} Get Feedback
                  </Button>
                  {prepFeedback[item.id] && <p className="text-xs text-amber-800" data-testid={`background-feedback-text-${item.id}`}>{prepFeedback[item.id]}</p>}
                </div>
              ))}
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm rounded-2xl">
            <CardHeader className="pb-3"><CardTitle className="text-base font-semibold">Common Interview Questions</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              {(resources.common_questions || []).map((q, i) => (
                <div key={`item-${i}`} className="p-3 rounded-xl bg-muted/30 space-y-2">
                  <p className="text-sm font-medium">{q}</p>
                  <DictationInput value={prepResponses[`question_${i}`] || ''} onChange={(cleaned) => setPrepResponses(prev => ({ ...prev, [`question_${i}`]: cleaned }))} rows={2} placeholder="Your answer..." data-testid={`common-question-${i}`} />
                  <Button size="sm" className="rounded-full" onClick={() => requestCoachFeedback(`question_${i}`, q, prepResponses[`question_${i}`] || '', 'common_question')} disabled={prepFeedbackLoading[`question_${i}`]} data-testid={`common-feedback-${i}`}>
                    {prepFeedbackLoading[`question_${i}`] ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />} Get Feedback
                  </Button>
                  {prepFeedback[`question_${i}`] && <p className="text-xs text-slate-700" data-testid={`common-feedback-text-${i}`}>{prepFeedback[`question_${i}`]}</p>}
                </div>
              ))}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
